import React from 'react';

import { sendSignupFlowEvent } from '@authFlow/utils/signupFlowEvent';
import { sendEvent } from '@utils/sendEvent';
import history from '@authFlow/App/history';
import sendSignupGA4Event from '@authFlow/utils/analytics/sendSignupGA4Event';
import { toolkitRedirects } from '@authFlow/utils/toolkitRedirects';
import { isStepRequired } from '@authFlow/utils/signupHelpers';
import isDesktopRecommendedStepRequired from '@authFlow/utils/isDesktopRecommendedStepRequired';

import { storageManager } from './SessionStorageManager';

export const redirectAndClear = () => {
  if (
    isStepRequired('marketingSource') &&
    history.location.pathname !== '/signup/marketing-source/'
  ) {
    storageManager.set({ marketingSource: 'off' });
    history.push('/signup/marketing-source/');
    return;
  }

  if (
    isDesktopRecommendedStepRequired() &&
    history.location.pathname !== '/signup/desktop-recommended/'
  ) {
    history.push('/signup/desktop-recommended/');
    return;
  }

  const redirectTo = getRedirectTo() || '/';
  sendSignupFlowEvent('flow:finish');
  sendSignupGA4Event('send', {
    item_label: 'finish',
    status: 'success',
  });
  storageManager.removeAll();
  window.location.assign(redirectTo);
};

export const redirectAndClearAfterLogin = () => {
  const redirectTo = storageManager.get('redirectTo') || '/';

  storageManager.removeAll();
  window.location.assign(redirectTo);
};

export const onLogoClick = (e: React.SyntheticEvent<HTMLAnchorElement>) => {
  e.preventDefault();

  sendEvent('Navigation:top_menu', 'link.click', 'logo');

  storageManager.removeAll();
  window.location.assign('/');
};

export const isDomain = (str: string): boolean => {
  const domainRegExp = new RegExp('^\\S+\\.\\S{2,}$', 'ig');
  return domainRegExp.test(str);
};

export const getRedirectTo = () => {
  const isTrial = storageManager.get('paymentStatus') === 'trial';
  const toolkitParameter = storageManager.get('toolkit');
  const custom = storageManager.get('custom');
  const { plan, type: toolkit } = storageManager.get('chosenTrial') || {};
  const fromTheMainPage = storageManager.get('src') === 'main_cta';
  const redirectTo = storageManager.get('redirectTo') || '';
  const personalisedPlan = storageManager.get('userData')?.personalisedPlan;

  if (personalisedPlan === 'semrush-one') {
    return redirectTo ? redirectTo : '/seo/';
  }

  if (custom === 'ai-seo') {
    const { domain } = storageManager.get('aiSeoData') || {};
    const query = isDomain(domain) ? `?domain=${domain}` : '';

    if (redirectTo && /^\/seo(\/)?$/.test(redirectTo)) {
      return `${redirectTo}${query}`;
    }

    return redirectTo ? redirectTo : `/seo/${query}`;
  }

  if (
    (toolkitParameter && redirectTo && !toolkit && !plan) ||
    (custom && !custom.includes('-toolkit'))
  )
    return redirectTo ? redirectTo : null;

  const getQParam = () => {
    const query = redirectTo.split('?')[1];
    return query ? new URLSearchParams(query).get('q') : null;
  };

  const qParam = getQParam();

  const withDomain =
    fromTheMainPage && redirectTo.startsWith('/analytics/overview') && !!qParam;
  const withKeywords =
    fromTheMainPage &&
    redirectTo.startsWith('/analytics/keywordoverview') &&
    !!qParam;

  if (redirectTo && !withDomain && !withKeywords) return redirectTo;

  const getRedirect = ({
    customToolkit,
    hasDomain,
    hasKeywords,
    isTrial,
    plan,
  }): string | null => {
    const type = hasDomain ? 'domain' : hasKeywords ? 'keywords' : 'default';
    const userTrialStatus = isTrial ? 'trial' : 'free';

    const section =
      toolkitRedirects?.[customToolkit]?.[type]?.[userTrialStatus];

    if (!section) return redirectTo ? redirectTo : null;

    if (typeof section === 'object' && plan) {
      return section[plan] || (redirectTo ? redirectTo : null);
    }

    if (typeof section === 'string') {
      return section;
    }

    return redirectTo ? redirectTo : null;
  };

  const currentToolkitInfo = {
    customToolkit: toolkit || toolkitParameter + '-toolkit',
    hasDomain: withDomain,
    hasKeywords: withKeywords,
    isTrial,
    plan,
  };

  return getRedirect(currentToolkitInfo);
};
